import { Fragment, ReactNode } from "react";
import styles from "./OverviewMediaSwitcher.module.scss";
import CubeIcon from "@Icons/CubeIcon";
import FloorPlanIcon from "@Icons/FloorPlanIcon";
import MapIcon from "@Icons/MapIcon";
import PhotoIcon from "@Icons/PhotoIcon";
import VideoPlayIcon from "@Icons/VideoPlayIcon";
import { PAGE } from "constants/configConstants";
import gtagEventEmitter from "helpers/gtagEventEmitter";
import useTranslationHook from "hooks/useTranslationHook";
import wrapperHOC from "@Utils/wrapperHOC";
import { OverviewGalleryCounts } from "../../utils/buildOverviewGalleryTiles";
import {
  getOverviewGalleryEventProps,
  HeroMediaButton,
  trackHeroMediaButtonClick,
} from "../../utils/galleryEvents";

interface SwitcherButton {
  button: HeroMediaButton;
  label: string;
  // Today's ActionButtonSection label, kept as the legacy `clicked_<label>_tag` action so
  // existing reports keep counting (E-4). Set only where it differs from `label`.
  legacyLabel?: string;
  icon: ReactNode;
  count?: number;
  show: boolean;
  onClick: () => void;
}

const OverviewMediaSwitcher = ({
  counts,
  hasVideos,
  has3D,
  propertyId,
  onOpenGallery,
  onScrollToMap,
  onScrollToFloorPlans,
}: {
  counts: OverviewGalleryCounts;
  hasVideos: boolean;
  has3D: boolean;
  propertyId: string | number;
  onOpenGallery: (tab: "all" | "videos" | "3D") => void;
  onScrollToMap: () => void;
  onScrollToFloorPlans: () => void;
}) => {
  const { t } = useTranslationHook();

  const buttons: SwitcherButton[] = [
    {
      button: "photos",
      label: t("propertypage.components.actionbuttonsection.photos"),
      icon: <PhotoIcon />,
      count: counts.photos,
      show: true,
      onClick: () => onOpenGallery("all"),
    },
    {
      button: "videos",
      label: t("propertypage.components.actionbuttonsection.videos"),
      icon: <VideoPlayIcon />,
      count: counts.videos,
      show: hasVideos,
      onClick: () => onOpenGallery("videos"),
    },
    {
      button: "3d",
      label: t("propertypage.overviewgallery.3dview"),
      legacyLabel: t("propertypage.components.actionbuttonsection.views"),
      icon: <CubeIcon />,
      count: counts.views3d,
      show: has3D,
      onClick: () => onOpenGallery("3D"),
    },
    {
      button: "floor_plan",
      label: t("propertypage.overviewgallery.floorplans"),
      legacyLabel: t("propertypage.components.actionbuttonsection.floorplan"),
      icon: <FloorPlanIcon size="18" />,
      // A site plan alone shows the button without a count.
      count: counts.floorPlans || undefined,
      show: counts.hasFloorPlans,
      onClick: onScrollToFloorPlans,
    },
    {
      button: "map",
      label: t("propertypage.components.actionbuttonsection.mapview"),
      icon: <MapIcon />,
      show: true,
      onClick: onScrollToMap,
    },
  ];

  const handleClick = ({ button, label, legacyLabel = label, count, onClick }: SwitcherButton) => {
    onClick();
    gtagEventEmitter({
      action: `clicked_${legacyLabel?.replace(" ", "_")}_tag`,
      category: PAGE.INVENTORY,
      label: legacyLabel,
      ...getOverviewGalleryEventProps(),
    });
    trackHeroMediaButtonClick({ propertyId, button, count });
  };

  const visibleButtons = buttons.filter(({ show }) => show);

  return (
    <div className={styles.switcher} data-testid="overview-gallery-media-switcher">
      {visibleButtons.map((item, index) => {
        const text = item.count !== undefined ? `${item.label} (${item.count})` : item.label;
        return (
          <Fragment key={item.button}>
            {index > 0 && <span className={styles.divider} aria-hidden="true" />}
            <button
              type="button"
              className={styles.button}
              onClick={() => handleClick(item)}
              data-testid={`overview-gallery-switcher-${item.button}`}
            >
              <span className={styles.icon} aria-hidden="true">
                {item.icon}
              </span>
              {text}
            </button>
          </Fragment>
        );
      })}
    </div>
  );
};

export default wrapperHOC(OverviewMediaSwitcher, {
  componentName: "OverviewMediaSwitcher-PropertyPage",
  showForChina: false,
  showForWhiteLable: false,
  height: "56px",
});
